import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Spinner, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import config from '../../config';

const EDU_HTTPS_SETTINGS_API = `${config.backendUrl}/api/edu-https-settings`;

const defaultForm = {
  enabled: false,
  school_code: '',
};

export default function EduHttpsSetting() {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const applyResponseToForm = useCallback((data) => {
    if (!data || typeof data !== 'object') return;

    setForm({
      enabled: Boolean(data.enabled),
      school_code: (data.school_code ?? '').toString(),
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        const res = await axios.get(EDU_HTTPS_SETTINGS_API);

        if (!cancelled && res.data) {
          applyResponseToForm(res.data);
        }
      } catch (e) {
        if (!cancelled) {
          console.warn('EDU HTTPS 설정 조회 실패:', e);
          alert('EDU HTTPS 설정을 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [applyResponseToForm]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!form.enabled) return true;

    if (!form.school_code.trim()) {
      alert('전송을 사용할 때는 학교 기관코드를 입력하세요.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSaving(true);

    const payload = {
      enabled: Boolean(form.enabled),
      school_code: form.school_code.trim(),
    };

    try {
      await axios.post(EDU_HTTPS_SETTINGS_API, payload);
      alert('EDU HTTPS 설정이 저장되었습니다.');
    } catch (err) {
      console.error('EDU HTTPS 설정 저장 실패:', err);

      const detail =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message;

      alert(`저장에 실패했습니다. ${detail || ''}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="edu-setting-loading">
        <Spinner animation="border" size="sm" />
        <span>설정 불러오는 중…</span>
      </div>
    );
  }

  return (
    <div className="edu-setting-wrap">
      <style>
        {`
          .edu-setting-wrap {
            width: 100%;
            max-width: 860px;
            padding: 6px 0 4px;
          }

          .edu-setting-loading {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 0;
            color: #6c757d;
            font-size: 14px;
          }

          .edu-setting-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            margin-bottom: 18px;
            padding: 14px 16px;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            background: #f8fafc;
          }

          .edu-setting-title-box {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .edu-setting-title {
            font-size: 16px;
            font-weight: 700;
            color: #111827;
          }

          .edu-setting-desc {
            font-size: 13px;
            color: #6b7280;
            line-height: 1.4;
          }

          .edu-setting-switch {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-shrink: 0;
          }

          .edu-setting-switch .form-check {
            margin: 0;
            min-height: 0;
            padding-left: 0;
          }

          .edu-setting-switch .form-check-input {
            width: 56px;
            height: 28px;
            margin: 0;
            cursor: pointer;
          }

          .edu-setting-switch .form-check-label {
            display: none;
          }

          .edu-switch-text {
            font-size: 14px;
            font-weight: 700;
            color: #2563eb;
            min-width: 48px;
          }

          .edu-switch-text.off {
            color: #6b7280;
          }

          .edu-setting-card {
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 18px;
            background: #ffffff;
          }

          .edu-form-label {
            font-size: 14px;
            font-weight: 700;
            color: #1f2937;
            margin-bottom: 7px;
          }

          .edu-form-control {
            max-width: 360px;
            height: 38px;
            font-size: 14px;
          }

          .edu-form-help {
            display: block;
            margin-top: 5px;
            font-size: 12px;
            color: #6b7280;
          }

          .edu-save-area {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-top: 16px;
          }

          .edu-save-button {
            min-width: 82px;
            height: 38px;
            font-weight: 700;
          }

          @media (max-width: 768px) {
            .edu-setting-wrap {
              max-width: 100%;
            }

            .edu-setting-header {
              align-items: flex-start;
              flex-direction: column;
            }

            .edu-setting-switch {
              width: 100%;
              justify-content: flex-start;
            }

            .edu-form-control {
              max-width: 100%;
            }
          }
        `}
      </style>

      <Form onSubmit={handleSubmit}>
        <div className="edu-setting-header">
          <div className="edu-setting-title-box">
            <div className="edu-setting-title">EDU HTTPS 전송 설정</div>
            <div className="edu-setting-desc">
              학교 기관코드와 환경센서·인버터 데이터를 EDU 서버로 주기 전송합니다.
            </div>
          </div>

          <div className="edu-setting-switch">
            <Form.Check
              type="switch"
              id="edu-https-enabled"
              checked={form.enabled}
              onChange={(e) => handleChange('enabled', e.target.checked)}
            />
            <span className={`edu-switch-text ${form.enabled ? '' : 'off'}`}>
              {form.enabled ? '사용' : '미사용'}
            </span>
          </div>
        </div>

        <div className="edu-setting-card">
          <Row className="g-3">
            <Col md={8}>
              <Form.Group controlId="eduHttpsSchoolCode">
                <Form.Label className="edu-form-label">학교 기관코드</Form.Label>
                <Form.Control
                  className="edu-form-control"
                  type="text"
                  placeholder="예: SCH12345678"
                  value={form.school_code}
                  onChange={(e) => handleChange('school_code', e.target.value)}
                  disabled={!form.enabled}
                  autoComplete="off"
                />
                <Form.Text className="edu-form-help">
                  EDU 서버에 등록된 학교 기관코드를 입력합니다.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <div className="edu-save-area">
            <Button
              type="submit"
              variant="primary"
              disabled={saving}
              className="edu-save-button"
            >
              {saving ? '저장 중…' : '저장'}
            </Button>

            {!form.enabled && (
              <span className="edu-form-help">
                전송 사용을 켜야 기관코드를 수정할 수 있습니다.
              </span>
            )}
          </div>
        </div>
      </Form>
    </div>
  );
}
